import { useState, useMemo, useEffect } from "react";
import { useAuth, COLORS, isAccessValid } from "./AuthContext";
import { dbFetchSessionLogs, supabase, dbFetchAllRequests, dbApproveRequest, dbRejectRequest, dbFetchBusinessForms, dbUpdateEnquiryStatus, dbFetchWaLeads, dbUpdateWaLeadStatus } from "./supabase";
import LeadDashboard from "./LeadDashboard";

const fmt      = (n)    => "₹" + Number(n).toLocaleString("en-IN");
const initials = (name) => name.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();

// Fetch subscribers + leads for all providers in one batch
const fetchAllProviderData = async (providerIds) => {
  const [subRes, leadRes] = await Promise.all([
    supabase.from("mealbox_subscribers").select("id,user_id,status,amount,name,phone,plan,type,delivery_area,email").in("user_id", providerIds),
    supabase.from("mealbox_leads").select("id,user_id,status").in("user_id", providerIds),
  ]);
  const subs  = subRes.data  || [];
  const leads = leadRes.data || [];
  return { subs, leads };
};

const STATUS_STYLE = {
  Active:   { bg: "#EAF3DE", color: "#27500A" },
  Pending:  { bg: "#FAEEDA", color: "#633806" },
  Overdue:  { bg: "#FCEBEB", color: "#791F1F" },
  Paused:   { bg: "#E6F1FB", color: "#0C447C" },
  Inactive: { bg: "#F1EFE8", color: "#5F5E5A" },
};

const emptyUser = (existingIds) => {
  const num = existingIds.length + 1;
  return {
    id: `u${Date.now()}`, loginId: "", password: "",
    name: "", owner: "", city: "",
    color: COLORS[num % COLORS.length],
    active: true, accessExpiry: "",
  };
};

// ── User Form Modal ───────────────────────────────────────────────────────────
function UserFormModal({ user: editUser, isEdit, onSave, onClose, existingLogins }) {
  const [form, setForm] = useState({ ...editUser });
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.name.trim())    return setError("Business name is required.");
    if (!form.loginId.trim()) return setError("Login ID is required.");
    if (!form.password.trim()) return setError("Password is required.");
    const dupe = existingLogins.find(l => l.loginId === form.loginId.trim() && l.id !== form.id);
    if (dupe) return setError("Login ID already taken. Choose another.");
    onSave({ ...form, loginId: form.loginId.trim() });
  };

  const fields = [
    { label: "Business name *",  key: "name",     type: "text",     placeholder: "e.g. Spice Kitchen" },
    { label: "Owner name",       key: "owner",    type: "text",     placeholder: "e.g. Ramesh Kumar"  },
    { label: "City",             key: "city",     type: "text",     placeholder: "e.g. Chennai"       },
    { label: "Login ID *",       key: "loginId",  type: "text",     placeholder: "e.g. spice_kitchen" },
    { label: "Password *",       key: "password", type: "text",     placeholder: "min 6 characters"   },
    { label: "Access expiry",    key: "accessExpiry", type: "date", placeholder: ""                    },
  ];

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <div className="modal-box" style={{ background:"#FFF", borderRadius:16, padding:"2rem" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem" }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>{isEdit ? "Edit provider" : "Add new provider"}</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#A8A29E" }}>✕</button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          {fields.map(f => (
            <div key={f.key} style={{ gridColumn: ["name","loginId","password"].includes(f.key) ? "1/-1" : "auto" }}>
              <label style={{ display:"block", fontSize:12, color:"#78716C", marginBottom:5 }}>{f.label}</label>
              <input type={f.type} value={form[f.key] || ""} placeholder={f.placeholder}
                onChange={e => { set(f.key, e.target.value); setError(""); }}
                style={{ width:"100%", padding:"8px 10px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", outline:"none", boxSizing:"border-box" }} />
            </div>
          ))}

          {/* Color picker */}
          <div style={{ gridColumn:"1/-1" }}>
            <label style={{ display:"block", fontSize:12, color:"#78716C", marginBottom:8 }}>Brand color</label>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {COLORS.map(c => (
                <button key={c} onClick={() => set("color", c)} style={{
                  width:28, height:28, borderRadius:"50%", background:c, border:`3px solid ${form.color === c ? "#1C1917" : "transparent"}`,
                  cursor:"pointer", flexShrink:0,
                }} />
              ))}
            </div>
          </div>

          {/* Access toggle */}
          <div style={{ gridColumn:"1/-1", display:"flex", alignItems:"center", justifyContent:"space-between", background:"#F7F6F2", borderRadius:10, padding:"10px 14px" }}>
            <div>
              <div style={{ fontSize:13, fontWeight:500, color:"#1C1917" }}>Account access</div>
              <div style={{ fontSize:11, color:"#A8A29E" }}>Suspended accounts cannot log in</div>
            </div>
            <button onClick={() => set("active", !form.active)} style={{
              padding:"6px 16px", fontSize:13, borderRadius:20, border:"none", cursor:"pointer", fontWeight:500,
              background: form.active ? "#EAF3DE" : "#FCEBEB",
              color:       form.active ? "#27500A" : "#A32D2D",
            }}>
              {form.active ? "Active" : "Suspended"}
            </button>
          </div>
        </div>

        {error && <div style={{ marginTop:12, background:"#FCEBEB", border:"0.5px solid #F09595", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#A32D2D" }}>{error}</div>}

        <div style={{ display:"flex", gap:8, marginTop:"1.5rem" }}>
          <button onClick={save} style={{ flex:1, padding:"10px", fontSize:13, fontWeight:500, borderRadius:8, border:"none", background:"#1C1917", color:"#FFF", cursor:"pointer" }}>
            {isEdit ? "Save changes" : "Create provider"}
          </button>
          <button onClick={onClose} style={{ padding:"10px 18px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", cursor:"pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── ProviderDetail — tabbed Subscribers + Leads view ─────────────────────────
const LEAD_STATUS_STYLE = {
  New:        { bg:"#E6F1FB", color:"#0C447C" },
  Contacted:  { bg:"#FAEEDA", color:"#633806" },
  Interested: { bg:"#EEEDFE", color:"#3C3489" },
  Trial:      { bg:"#EAF3DE", color:"#085041" },
  Converted:  { bg:"#EAF3DE", color:"#27500A" },
  Lost:       { bg:"#F1EFE8", color:"#5F5E5A" },
};

function ProviderDetail({ provider, providerData, search, setSearch }) {
  const [tab, setTab] = useState("subscribers"); // subscribers | leads

  const subs = useMemo(() => {
    const q = search.toLowerCase();
    return (provider.subs || []).filter(s =>
      !q || s.name.toLowerCase().includes(q) || (s.phone||"").includes(q)
    );
  }, [provider, search]);

  const leads = useMemo(() => {
    const raw = providerData.leads.filter(l => l.user_id === provider.id);
    const q   = search.toLowerCase();
    return raw.filter(l =>
      !q || (l.name||"").toLowerCase().includes(q) || (l.phone||"").includes(q)
    );
  }, [provider, providerData, search]);

  return (
    <>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:500, color:"#1C1917" }}>{provider.name}</div>
          <div style={{ fontSize:12, color:"#78716C" }}>
            {provider.total} subscribers · {provider.leadsTotal} leads · {provider.city}
          </div>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={`Search ${tab}…`}
          style={{ padding:"7px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", outline:"none", width:180 }} />
      </div>

      {/* Sub-tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:12 }}>
        {[
          { key:"subscribers", label:`Subscribers (${provider.total})` },
          { key:"leads",       label:`Leads (${provider.leadsTotal})` },
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }}
            style={{ padding:"6px 16px", fontSize:12, borderRadius:20, cursor:"pointer", border:"0.5px solid #E7E5E0",
              background: tab===t.key?"#1C1917":"#FFF",
              color:      tab===t.key?"#FFF":"#78716C",
              fontWeight: tab===t.key?500:400 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Subscribers table ── */}
      {tab === "subscribers" && (
        subs.length === 0 ? (
          <div style={{ background:"#FFF", border:"0.5px solid #E7E5E0", borderRadius:12, padding:"3rem", textAlign:"center", color:"#A8A29E", fontSize:13 }}>
            {provider.total === 0 ? "No subscribers yet" : "No results found"}
          </div>
        ) : (
          <div style={{ background:"#FFF", borderRadius:12, border:"0.5px solid #E7E5E0", overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:600 }}>
              <thead>
                <tr style={{ background:"#F7F6F2", borderBottom:"0.5px solid #E7E5E0" }}>
                  {["Name","Phone","Plan","Type","Area","Amount","Status"].map(h => (
                    <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontWeight:500, fontSize:12, color:"#78716C", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => {
                  const st = STATUS_STYLE[s.status] || STATUS_STYLE.Pending;
                  return (
                    <tr key={s.id} style={{ borderBottom: i < subs.length-1 ? "0.5px solid #F3F2EE" : "none" }}>
                      <td style={{ padding:"9px 14px", fontWeight:500, color:"#1C1917" }}>
                        {s.name}
                        <div style={{ fontSize:11, color:"#A8A29E", fontWeight:400 }}>{s.email||""}</div>
                      </td>
                      <td style={{ padding:"9px 14px", color:"#78716C", fontSize:12 }}>{s.phone}</td>
                      <td style={{ padding:"9px 14px", color:"#44403C" }}>{s.plan}</td>
                      <td style={{ padding:"9px 14px", color:"#44403C" }}>{s.type}</td>
                      <td style={{ padding:"9px 14px", color:"#78716C", fontSize:12 }}>{s.deliveryArea}</td>
                      <td style={{ padding:"9px 14px", fontWeight:500 }}>{fmt(s.amount||0)}</td>
                      <td style={{ padding:"9px 14px" }}>
                        <span style={{ fontSize:11, fontWeight:500, padding:"3px 10px", borderRadius:20, background:st.bg, color:st.color }}>{s.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── Leads table ── */}
      {tab === "leads" && (
        leads.length === 0 ? (
          <div style={{ background:"#FFF", border:"0.5px solid #E7E5E0", borderRadius:12, padding:"3rem", textAlign:"center", color:"#A8A29E", fontSize:13 }}>
            {provider.leadsTotal === 0 ? "No leads yet" : "No results found"}
          </div>
        ) : (
          <div style={{ background:"#FFF", borderRadius:12, border:"0.5px solid #E7E5E0", overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:700 }}>
              <thead>
                <tr style={{ background:"#F7F6F2", borderBottom:"0.5px solid #E7E5E0" }}>
                  {["Name","Phone","Email","Source","Interest","Area","Follow-up","Status","Notes"].map(h => (
                    <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontWeight:500, fontSize:12, color:"#78716C", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => {
                  const st = LEAD_STATUS_STYLE[l.status] || LEAD_STATUS_STYLE.New;
                  const overdue = l.follow_up && new Date(l.follow_up) < new Date(new Date().toISOString().slice(0,10));
                  return (
                    <tr key={l.id} style={{ borderBottom: i < leads.length-1 ? "0.5px solid #F3F2EE" : "none" }}>
                      <td style={{ padding:"9px 14px", fontWeight:500, color:"#1C1917", whiteSpace:"nowrap" }}>{l.name}</td>
                      <td style={{ padding:"9px 14px", color:"#78716C", fontSize:12 }}>{l.phone||"—"}</td>
                      <td style={{ padding:"9px 14px", color:"#78716C", fontSize:11 }}>{l.email||"—"}</td>
                      <td style={{ padding:"9px 14px", color:"#44403C", fontSize:12 }}>{l.source||"—"}</td>
                      <td style={{ padding:"9px 14px", color:"#44403C", fontSize:12 }}>{l.meal_interest||"—"}</td>
                      <td style={{ padding:"9px 14px", color:"#78716C", fontSize:12 }}>{l.area||"—"}</td>
                      <td style={{ padding:"9px 14px", fontSize:12, whiteSpace:"nowrap",
                        color: overdue ? "#A32D2D" : "#44403C", fontWeight: overdue ? 500 : 400 }}>
                        {l.follow_up ? (overdue ? "⚠ " : "") + new Date(l.follow_up).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—"}
                      </td>
                      <td style={{ padding:"9px 14px" }}>
                        <span style={{ fontSize:11, fontWeight:500, padding:"3px 10px", borderRadius:20, background:st.bg, color:st.color, whiteSpace:"nowrap" }}>{l.status}</span>
                      </td>
                      <td style={{ padding:"9px 14px", fontSize:11, color:"#78716C", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {l.notes||"—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </>
  );
}

// ── Main SuperAdmin ───────────────────────────────────────────────────────────
export default function SuperAdmin() {
  const { logout, providerUsers, setUsers, removeUser, users } = useAuth();
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [search,       setSearch]       = useState("");
  const [showForm,     setShowForm]     = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm]   = useState({ current: "", next: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [activeTab, setActiveTab] = useState("providers");
  const [sessionLogs,    setSessionLogs]    = useState([]);
  const [loadingLogs,    setLoadingLogs]    = useState(false);
  const [sessionSearch,  setSessionSearch]  = useState("");
  const [sessionFilter,  setSessionFilter]  = useState("All");
  const [requests,       setRequests]       = useState([]);
  const [loadingReqs,    setLoadingReqs]    = useState(false);
  const [rejectTarget,   setRejectTarget]   = useState(null);
  const [rejectNote,     setRejectNote]     = useState("");
  const [debugLog,       setDebugLog]       = useState([]);
  const [debugging,      setDebugging]      = useState(false);
  const [landingUrl,     setLandingUrl]     = useState("https://dietfooddelivery.in");
  // Provider Listing tab state
  const [listingProviders, setListingProviders] = useState([]);
  const [listingLoading,   setListingLoading]   = useState(false);
  const [listingSearch,    setListingSearch]    = useState("");
  const [showListForm,     setShowListForm]     = useState(false);
  const [editListTarget,   setEditListTarget]   = useState(null);
  const [listForm,         setListForm]         = useState({});
  const [listStep,         setListStep]         = useState(0);
  const [listSaving,       setListSaving]       = useState(false);
  const [listError,        setListError]        = useState("");
  const [placeQuery,       setPlaceQuery]       = useState("");
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [placeSearching,   setPlaceSearching]   = useState(false);
  const [placeSelected,    setPlaceSelected]    = useState(false);
  const [formSubmissions,  setFormSubmissions]  = useState([]);
  const [waLeads,          setWaLeads]          = useState([]);
  const [waLeadsLoading,   setWaLeadsLoading]   = useState(false);
  const [loadingForms,     setLoadingForms]     = useState(false);
  const [listUploading,    setListUploading]    = useState(false);
  const [logoUploading,    setLogoUploading]    = useState(false);
  // Enquiries tab state
  const [enquiries,        setEnquiries]        = useState([]);
  const [enquiryLoading,   setEnquiryLoading]   = useState(false);
  const [providerData,   setProviderData]   = useState({ subs: [], leads: [] });
  const [loadingData,    setLoadingData]    = useState(true);

  // Fetch all subscriber + lead data for all providers
  useEffect(() => {
    const ids = providerUsers.map(u => u.id);
    if (ids.length === 0) { setLoadingData(false); return; }
    fetchAllProviderData(ids)
      .then(data => { setProviderData(data); setLoadingData(false); })
      .catch(() => setLoadingData(false));
  }, [providerUsers]);

  const providers = useMemo(() => providerUsers.map(u => {
    const subs    = providerData.subs.filter(s => s.user_id === u.id);
    const leads   = providerData.leads.filter(l => l.user_id === u.id);
    const active  = subs.filter(s => s.status === "Active");
    const overdue = subs.filter(s => s.status === "Overdue");
    const revenue = active.reduce((a, s) => a + (Number(s.amount) || 0), 0);
    const expired = u.accessExpiry && new Date(u.accessExpiry) < new Date();
    const newLeads = leads.filter(l => l.status === "New" || l.status === "Contacted").length;
    // Map subs to camelCase for the subscriber table
    const subsForTable = subs.map(s => ({
      id: s.id, name: s.name, phone: s.phone, plan: s.plan,
      type: s.type, deliveryArea: s.delivery_area, amount: s.amount,
      status: s.status, email: s.email,
    }));
    return { ...u, subs: subsForTable, activeCount: active.length, overdueCount: overdue.length,
      revenue, total: subs.length, expired, leadsTotal: leads.length, leadsNew: newLeads };
  }), [providerUsers, providerData]);

  const totalStats = useMemo(() => ({
    total:       providers.length,
    active:      providers.filter(p => p.active && !p.expired).length,
    suspended:   providers.filter(p => !p.active || p.expired).length,
    subscribers: providers.reduce((a, p) => a + p.total, 0),
    revenue:     providers.reduce((a, p) => a + p.revenue, 0),
    leads:       providers.reduce((a, p) => a + p.leadsTotal, 0),
  }), [providers]);

  // Fetch all providers for listing tab
  useEffect(() => {
    if (activeTab !== "listing") return;
    setListingLoading(true);
    supabase.from("mealbox_users")
      .select("id,name,owner,city,color,is_listed,active,tagline,description,whatsapp,diet_types,meal_types,plans,areas_served,menu_images,logo_url,rating,review_count")
      .neq("id","superadmin").order("name")
      .then(({ data }) => { setListingProviders(data||[]); setListingLoading(false); });
  }, [activeTab]);

  // Fetch enquiries
  useEffect(() => {
    if (activeTab !== "enquiries") return;
    setEnquiryLoading(true);
    dbFetchBusinessForms().then(data => { setEnquiries(data); setEnquiryLoading(false); });
  }, [activeTab]);

  const saveListingProvider = async () => {
    if (!listForm.name?.trim()) { setListError("Business name is required."); return; }
    if (!listForm.city?.trim()) { setListError("City is required."); return; }
    setListSaving(true); setListError("");
    try {
      const isNew = !listForm.id;
      const payload = {
        name:         listForm.name?.trim()||"",
        owner:        listForm.owner?.trim()||"",
        city:         listForm.city?.trim()||"",
        tagline:      listForm.tagline?.trim()||"",
        description:  listForm.description?.trim()||"",
        whatsapp:     listForm.whatsapp?.trim()||"",
        color:        listForm.color||"#1A6B3C",
        logo_url:     listForm.logo_url||null,
        is_listed:    listForm.is_listed||false,
        active:       listForm.active !== false,
        diet_types:   Array.isArray(listForm.diet_types) ? listForm.diet_types : [],
        meal_types:   Array.isArray(listForm.meal_types) ? listForm.meal_types : [],
        areas_served: typeof listForm.areas_served==="string"
          ? listForm.areas_served.split(",").map(s=>s.trim()).filter(Boolean)
          : listForm.areas_served||[],
        plans:        listForm.plans||[],
        menu_images:  listForm.menu_images||[],
        rating:       Number(listForm.rating)||0,
        review_count: Number(listForm.review_count)||0,
      };
      if (isNew) {
        const newId   = "listed_" + Date.now();
        const loginId = (listForm.name.toLowerCase().replace(/[^a-z0-9]/g,"_").slice(0,15)) + "_" + Date.now().toString().slice(-4);
        await supabase.from("mealbox_users").insert({ ...payload, id:newId, login_id:loginId, password:"nologin" });
        setListingProviders(prev => [...prev, { ...payload, id:newId }]);
      } else {
        await supabase.from("mealbox_users").update(payload).eq("id", listForm.id);
        setListingProviders(prev => prev.map(p => p.id===listForm.id ? {...p,...payload} : p));
      }
      setShowListForm(false); setListForm({}); setListStep(0); setEditListTarget(null);
    } catch(e) { setListError(e.message||"Save failed"); }
    setListSaving(false);
  };

  const deleteListingProvider = async (id, name) => {
    if (!window.confirm("Delete " + name + "? This cannot be undone.")) return;
    try {
      await supabase.from("mealbox_users").delete().eq("id", id);
      setListingProviders(prev => prev.filter(p => p.id !== id));
    } catch(e) { alert("Delete failed: " + e.message); }
  };

  const toggleListingListed = async (p) => {
    try {
      await supabase.from("mealbox_users").update({ is_listed: !p.is_listed, active: true }).eq("id", p.id);
      setListingProviders(prev => prev.map(x => x.id===p.id ? {...x, is_listed:!p.is_listed} : x));
    } catch(e) { alert("Update failed: " + e.message); }
  };

  const uploadListingLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file."); return; }
    if (file.size > 3*1024*1024) { alert("Logo must be under 3MB."); return; }
    setLogoUploading(true);
    try {
      const ext  = file.name.split(".").pop();
      const path = `logos/${listForm.id||"new_"+Date.now()}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("menu-images").upload(path, file, { upsert:true });
      if (error) throw error;
      const { data:{ publicUrl } } = supabase.storage.from("menu-images").getPublicUrl(path);
      setListForm(f => ({ ...f, logo_url: publicUrl }));
    } catch(err) { alert("Logo upload failed: " + err.message); }
    setLogoUploading(false);
    e.target.value = "";
  };

  const uploadListingMenuImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file."); return; }
    if (file.size > 5*1024*1024) { alert("Image must be under 5MB."); return; }
    if ((listForm.menu_images||[]).length >= 4) { alert("Maximum 4 images."); return; }
    setListUploading(true);
    try {
      const ext  = file.name.split(".").pop();
      const path = `menus/${listForm.id||"new_"+Date.now()}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("menu-images").upload(path, file, { upsert:true });
      if (error) throw error;
      const { data:{ publicUrl } } = supabase.storage.from("menu-images").getPublicUrl(path);
      setListForm(f => ({ ...f, menu_images: [...(f.menu_images||[]), publicUrl] }));
    } catch(err) { alert("Upload failed: " + err.message); }
    setListUploading(false);
    e.target.value = "";
  };

  // Fetch form submissions when tab is opened
  useEffect(() => {
    if (activeTab !== "forms") return;
    setLoadingForms(true);
    dbFetchBusinessForms()
      .then(data => { setFormSubmissions(data); setLoadingForms(false); })
      .catch(() => setLoadingForms(false));
  }, [activeTab]);

  // Fetch marketplace requests when tab is opened
  useEffect(() => {
    if (activeTab !== "requests") return;
    setLoadingReqs(true);
    dbFetchAllRequests()
      .then(data => { setRequests(data); setLoadingReqs(false); })
      .catch(() => setLoadingReqs(false));
  }, [activeTab]);

  // Fetch session logs when tab is opened
  useEffect(() => {
    if (activeTab !== "sessions") return;
    setLoadingLogs(true);
    dbFetchSessionLogs(200)
      .then(data => { setSessionLogs(data); setLoadingLogs(false); })
      .catch(() => setLoadingLogs(false));
  }, [activeTab]);

  // Fetch WhatsApp leads when tab is opened
  useEffect(() => {
    if (activeTab !== "waleads" && activeTab !== "leaddash") return;
    setWaLeadsLoading(true);
    dbFetchWaLeads()
      .then(data => { setWaLeads(data); setWaLeadsLoading(false); })
      .catch(() => setWaLeadsLoading(false));
  }, [activeTab]);

  const filteredSessions = useMemo(() => {
    return sessionLogs.filter(l => {
      const q = sessionSearch.toLowerCase();
      if (q && !l.user_name?.toLowerCase().includes(q) && !l.user_id?.toLowerCase().includes(q)) return false;
      if (sessionFilter !== "All" && l.event !== sessionFilter) return false;
      return true;
    });
  }, [sessionLogs, sessionSearch, sessionFilter]);

  const approveRequest = async (req) => {
    try {
      await dbApproveRequest(req.id, req.user_id, req.payload);
      setRequests(prev => prev.map(r => r.id === req.id ? {...r, status:"approved"} : r));
    } catch(err) { alert("Approve failed: " + err.message); }
  };

  const rejectRequest = async () => {
    if (!rejectTarget) return;
    try {
      await dbRejectRequest(rejectTarget.id, rejectNote);
      setRequests(prev => prev.map(r => r.id === rejectTarget.id ? {...r, status:"rejected", note:rejectNote} : r));
      setRejectTarget(null); setRejectNote("");
    } catch(err) { alert("Reject failed: " + err.message); }
  };

  const openAdd  = () => { setEditTarget(emptyUser(users)); setShowForm(true); };
  const openEdit = (p) => { setEditTarget({ ...p }); setShowForm(true); };

  const saveUser = (data) => {
    setUsers(prev =>
      prev.find(u => u.id === data.id)
        ? prev.map(u => u.id === data.id ? data : u)
        : [...prev, data]
    );
    setShowForm(false);
    setEditTarget(null);
  };

  const toggleAccess = (id) => {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, active: !u.active } : u));
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this provider? Their subscriber data will remain in storage.")) return;
    try {
      await removeUser(id);
      if (selectedProvider?.id === id) setSelectedProvider(null);
    } catch (err) { alert("Delete failed: " + err.message); }
  };

  const changePassword = () => {
    const adminUser = users.find(u => u.id === "superadmin");
    if (!adminUser) return;
    if (pwForm.current !== adminUser.password) { setPwError("Current password is incorrect."); return; }
    if (pwForm.next.length < 6) { setPwError("New password must be at least 6 characters."); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError("Passwords do not match."); return; }
    setUsers(prev => prev.map(u => u.id === "superadmin" ? { ...u, password: pwForm.next } : u));
    setShowChangePw(false);
    setPwForm({ current: "", next: "", confirm: "" });
    setPwError("");
  };

  // ── Debug runner ─────────────────────────────────────────────────────────
  const runDebug = async () => {
    setDebugging(true);
    const log = [];
    const SB_URL = "https://kftxqbaraolupywxfrrv.supabase.co";

    const addLog = (status, title, detail, data) => {
      log.push({ status, title, detail, data, time: new Date().toLocaleTimeString() });
      setDebugLog([...log]);
    };

    // Test 1: Basic connection
    try {
      const { data, error } = await supabase.from("mealbox_users").select("id").limit(1);
      if (error) throw error;
      addLog("pass","Supabase connection","Connected successfully",null);
    } catch(e) { addLog("fail","Supabase connection",`Cannot connect: ${e.message}`,null); }

    // Test 2: All providers
    try {
      const { data, error } = await supabase.from("mealbox_users")
        .select("id,name,is_listed,active").neq("id","superadmin");
      if (error) throw error;
      addLog("pass",`Providers in DB (${data.length})`,
        "All provider accounts found",
        data.map(u=>({ id:u.id, name:u.name, is_listed:u.is_listed, active:u.active })));
    } catch(e) { addLog("fail","Providers in DB",`Query failed: ${e.message}`,null); }

    // Test 3: Listed providers
    try {
      const { data, error } = await supabase.from("mealbox_users")
        .select("id,name,is_listed,tagline,areas_served,diet_types,plans,whatsapp,color")
        .eq("is_listed",true).neq("id","superadmin");
      if (error) throw error;
      if (data.length === 0) {
        addLog("warn","Listed providers (0)",
          "No provider has is_listed=true. Run: UPDATE mealbox_users SET is_listed=true WHERE id='u1'",null);
      } else {
        addLog("pass",`Listed providers (${data.length})`,
          "These will show on landing page",
          data.map(u=>({ id:u.id, name:u.name, tagline:u.tagline||"MISSING", areas:(u.areas_served||[]).length, plans:(u.plans||[]).length, whatsapp:u.whatsapp||"MISSING" })));
      }
    } catch(e) { addLog("fail","Listed providers",`Query failed: ${e.message}`,null); }

    // Test 4: marketplace_providers view
    try {
      const { data, error } = await supabase.from("marketplace_providers").select("id,name").limit(10);
      if (error) throw error;
      data.length === 0
        ? addLog("warn","marketplace_providers view","View exists but 0 rows — check is_listed=true",null)
        : addLog("pass",`marketplace_providers view (${data.length} rows)`,"View working correctly",data);
    } catch(e) {
      addLog("warn","marketplace_providers view",`View missing (${e.message}). Landing page uses direct table query as fallback.`,null);
    }

    // Test 5: Required columns
    try {
      const { data, error } = await supabase.from("mealbox_users")
        .select("tagline,description,whatsapp,areas_served,diet_types,meal_types,plans,is_listed,menu_images,color")
        .eq("id","u1").single();
      if (error) throw error;
      const missing = ["tagline","description","whatsapp","areas_served","diet_types","meal_types","plans","is_listed","menu_images","color"]
        .filter(col => data[col] === undefined);
      missing.length > 0
        ? addLog("fail","Required columns",`Missing: ${missing.join(", ")} — run FIX_COLUMNS_AND_STORAGE.sql`,null)
        : addLog("pass","Required columns","All marketplace columns exist",null);
    } catch(e) { addLog("warn","Required columns",`Could not verify: ${e.message}`,null); }

    // Test 6: Direct REST API (exactly what landing page does)
    try {
      const { supabaseKey } = supabase;
      const shortKey = supabaseKey ? supabaseKey.substring(0,30)+"..." : "not found";
      addLog("info","Anon key",`Key: ${shortKey}`,null);
      const res = await fetch(
        `${SB_URL}/rest/v1/mealbox_users?select=id,name,is_listed&is_listed=eq.true&id=neq.superadmin`,
        { headers:{ apikey: supabaseKey, Accept:"application/json" } }
      );
      const body = await res.json();
      res.ok
        ? addLog("pass",`REST API returns ${body.length} provider(s)`,"Landing page fetch is working correctly",body)
        : addLog("fail","REST API failed",`Status ${res.status}: ${JSON.stringify(body)} — this is what the landing page gets`,null);
    } catch(e) { addLog("fail","REST API call",`Fetch error: ${e.message}`,null); }

    // Test 7: Requests table
    try {
      const { error } = await supabase.from("mealbox_marketplace_requests").select("id").limit(1);
      if (error) throw error;
      addLog("pass","Requests table","mealbox_marketplace_requests exists",null);
    } catch(e) { addLog("fail","Requests table",`Missing: ${e.message} — run MARKETPLACE_SQL.sql`,null); }

    setDebugging(false);
  };

  return (
    <div style={{ fontFamily:"'DM Sans','Segoe UI',sans-serif", minHeight:"100vh", background:"#F7F6F2" }}>

      {/* Top bar */}
      <div style={{ background:"#1C1917", padding:"0 2rem", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ fontSize:16, fontWeight:700, color:"#FFF", letterSpacing:"-0.02em" }}>MealBox</div>
          <div style={{ width:1, height:16, background:"#292524" }} />
          <div style={{ fontSize:13, color:"#78716C" }}>Super Admin Console</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => { setShowChangePw(true); setPwError(""); setPwForm({ current:"", next:"", confirm:"" }); }}
            style={{ fontSize:13, padding:"6px 14px", borderRadius:8, border:"0.5px solid #292524", background:"transparent", color:"#A8A29E", cursor:"pointer" }}>
            Change password
          </button>
          <button onClick={openAdd} style={{ fontSize:13, padding:"6px 14px", borderRadius:8, border:"none", background:"#FFF", color:"#1C1917", cursor:"pointer", fontWeight:500 }}>
            + Add provider
          </button>
          <button onClick={logout} style={{ fontSize:13, padding:"6px 14px", borderRadius:8, border:"0.5px solid #292524", background:"transparent", color:"#A8A29E", cursor:"pointer" }}>
            Sign out
          </button>
        </div>
      </div>

      <div className="page-wrap">

        {/* Header */}
        <div style={{ marginBottom:"1.5rem" }}>
          <h1 style={{ fontSize:22, fontWeight:600, letterSpacing:"-0.02em", margin:0 }}>Service Providers</h1>
          <p style={{ fontSize:13, color:"#78716C", margin:"4px 0 0" }}>Manage access, credentials and view subscriber data</p>
        </div>

        {/* Global stats */}
        <div className="stat-grid-5" style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, marginBottom:"1.5rem" }}>
          {[
            { label:"Total providers",   value: totalStats.total       },
            { label:"Active",            value: totalStats.active      },
            { label:"Suspended/expired", value: totalStats.suspended,  warn: totalStats.suspended > 0 },
            { label:"Total subscribers", value: totalStats.subscribers },
            { label:"Total leads",       value: totalStats.leads       },
            { label:"Combined revenue",  value: fmt(totalStats.revenue)},
          ].map(c => (
            <div key={c.label} style={{ background: c.warn ? "#FCEBEB" : "#FFF", border:`0.5px solid ${c.warn ? "#F09595" : "#E7E5E0"}`, borderRadius:12, padding:"1rem 1.25rem" }}>
              <div style={{ fontSize:12, color: c.warn ? "#A32D2D" : "#78716C", marginBottom:6 }}>{c.label}</div>
              <div style={{ fontSize:22, fontWeight:600, color: c.warn ? "#A32D2D" : "#1C1917" }}>{c.value}</div>
            </div>
          ))}
        </div>

        {/* ── Tab switcher ── */}
        <div style={{ display:"flex", gap:4, marginBottom:"1.5rem", borderBottom:"0.5px solid #E7E5E0", paddingBottom:0, flexWrap:"wrap" }}>
          {[
            { key:"providers", label:"Providers" },
            { key:"requests",  label:"Requests", badge: requests.filter(r=>r.status==="pending").length || null },
            { key:"sessions",  label:"Session logs" },
            { key:"debug",     label:"🔧 Debug" },
            { key:"listing",   label:"📋 Provider Listing" },
            { key:"enquiries", label:"📩 Form Submissions", badge: enquiries.filter(e=>e.status==="new").length || null },
            { key:"waleads",   label:"💬 WhatsApp Leads",  badge: waLeads.filter(w=>!w.seen).length || null },
            { key:"leaddash",  label:"📊 Lead Dashboard" },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              padding:"8px 18px", fontSize:13, borderRadius:"8px 8px 0 0", cursor:"pointer",
              border:"0.5px solid #E7E5E0", borderBottom: activeTab === t.key ? "2px solid #1C1917" : "0.5px solid #E7E5E0",
              background: activeTab === t.key ? "#FFF" : "transparent",
              color: activeTab === t.key ? "#1C1917" : "#78716C",
              fontWeight: activeTab === t.key ? 500 : 400, marginBottom: activeTab === t.key ? -1 : 0,
              display:"flex", alignItems:"center", gap:6,
            }}>
              {t.label}
              {t.badge ? <span style={{background:"#A32D2D",color:"#FFF",fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10}}>{t.badge}</span> : null}
            </button>
          ))}
        </div>

        {/* ── PROVIDERS TAB — full list view ── */}
        {activeTab === "providers" && (
        <div>
          {/* List table */}
          <div style={{ background:"#FFF", borderRadius:12, border:"0.5px solid #E7E5E0", overflowX:"auto", marginBottom: selectedProvider ? 16 : 0 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#F7F6F2", borderBottom:"0.5px solid #E7E5E0" }}>
                  {["Provider","Login ID","Password","City","Customers","Active","Overdue","Leads","Revenue","Listed","Status","Actions"].map(h => (
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:500, fontSize:12, color:"#78716C", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {providers.map((p, i) => {
                  const valid   = isAccessValid(p);
                  const expired = p.accessExpiry && new Date(p.accessExpiry) < new Date();
                  const isSelected = selectedProvider?.id === p.id;
                  return (
                    <tr key={p.id}
                      onClick={() => { setSelectedProvider(isSelected ? null : p); setSearch(""); }}
                      style={{ borderBottom: i < providers.length-1 ? "0.5px solid #F3F2EE" : "none",
                        cursor:"pointer", background: isSelected ? "#F7F6F2" : "transparent",
                        opacity: valid ? 1 : 0.65 }}>

                      {/* Provider name + owner */}
                      <td style={{ padding:"11px 14px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:32, height:32, borderRadius:"50%", background:p.color+"22", color:p.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>
                            {initials(p.name)}
                          </div>
                          <div>
                            <div style={{ fontWeight:500, color:"#1C1917", whiteSpace:"nowrap" }}>{p.name}</div>
                            <div style={{ fontSize:11, color:"#A8A29E" }}>{p.owner}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:"11px 14px", fontSize:12, color:"#78716C", whiteSpace:"nowrap" }}>{p.loginId}</td>
                      <td style={{ padding:"11px 14px", fontSize:12, color:"#78716C", whiteSpace:"nowrap" }}>
                        <span style={{ background:"#F7F6F2", borderRadius:6, padding:"3px 8px", fontFamily:"monospace", letterSpacing:"0.05em" }}>{p.password}</span>
                      </td>
                      <td style={{ padding:"11px 14px", fontSize:12, color:"#78716C" }}>{p.city}</td>
                      <td style={{ padding:"11px 14px", fontWeight:600, color:"#1C1917", textAlign:"center" }}>{p.total}</td>
                      <td style={{ padding:"11px 14px", fontWeight:600, color:"#27500A", textAlign:"center" }}>{p.activeCount}</td>
                      <td style={{ padding:"11px 14px", fontWeight:600, color:p.overdueCount>0?"#A32D2D":"#A8A29E", textAlign:"center" }}>{p.overdueCount}</td>
                      <td style={{ padding:"11px 14px", textAlign:"center" }}>
                        <span style={{ fontWeight:600, color:"#0C447C" }}>{p.leadsTotal}</span>
                        {p.leadsNew>0 && <span style={{ fontSize:10, marginLeft:4, background:"#E6F1FB", color:"#0C447C", padding:"1px 5px", borderRadius:8 }}>{p.leadsNew} new</span>}
                      </td>
                      <td style={{ padding:"11px 14px", fontWeight:600, color:"#1C1917", whiteSpace:"nowrap" }}>{fmt(p.revenue)}</td>
                      <td style={{ padding:"11px 14px", textAlign:"center" }}>
                        {p.is_listed
                          ? <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, background:"#EAF3DE", color:"#27500A" }}>Listed</span>
                          : <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, background:"#F1EFE8", color:"#78716C" }}>Unlisted</span>}
                      </td>
                      <td style={{ padding:"11px 14px" }}>
                        {!valid
                          ? <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, background:"#FCEBEB", color:"#A32D2D" }}>{expired?"Expired":"Suspended"}</span>
                          : <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, background:"#EAF3DE", color:"#27500A" }}>Active</span>}
                        {p.accessExpiry && <div style={{ fontSize:10, color:"#A8A29E", marginTop:2 }}>Exp: {p.accessExpiry}</div>}
                      </td>
                      <td style={{ padding:"11px 14px" }} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:"flex", gap:5 }}>
                          <button onClick={()=>openEdit(p)} style={{ fontSize:11, padding:"4px 10px", borderRadius:6, border:"0.5px solid #E7E5E0", background:"#F7F6F2", color:"#44403C", cursor:"pointer" }}>Edit</button>
                          <button onClick={()=>toggleAccess(p.id)} style={{ fontSize:11, padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", background:valid?"#FCEBEB":"#EAF3DE", color:valid?"#A32D2D":"#27500A" }}>
                            {valid?"Suspend":"Restore"}
                          </button>
                          <button onClick={()=>deleteUser(p.id)} style={{ fontSize:11, padding:"4px 8px", borderRadius:6, border:"0.5px solid #F09595", background:"#FCEBEB", color:"#A32D2D", cursor:"pointer" }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Detail panel — slides in below table when a provider is selected */}
          {selectedProvider && (
            <div style={{ background:"#FFF", borderRadius:12, border:"0.5px solid #1C1917", padding:"1.25rem" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <div style={{ fontSize:14, fontWeight:500, color:"#1C1917" }}>{selectedProvider.name} — Details</div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
                    style={{ padding:"6px 10px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", outline:"none", width:160 }}/>
                  <button onClick={()=>setSelectedProvider(null)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#A8A29E" }}>✕</button>
                </div>
              </div>
              <ProviderDetail
                provider={providers.find(x=>x.id===selectedProvider.id)||selectedProvider}
                providerData={providerData}
                search={search}
                setSearch={setSearch}
              />
            </div>
          )}
        </div>
        )} {/* end providers tab */}

        {/* ── REQUESTS TAB ── */}
        {activeTab === "requests" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div style={{ fontSize:13, color:"#78716C" }}>
                Marketplace profile change requests from service providers
              </div>
              <button onClick={() => { setLoadingReqs(true); dbFetchAllRequests().then(d=>{setRequests(d);setLoadingReqs(false);}); }}
                style={{ padding:"7px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", cursor:"pointer", color:"#44403C" }}>
                ↻ Refresh
              </button>
            </div>

            {loadingReqs ? (
              <div style={{ textAlign:"center", padding:"3rem", color:"#A8A29E", fontSize:13 }}>Loading requests…</div>
            ) : requests.length === 0 ? (
              <div style={{ background:"#FFF", border:"0.5px solid #E7E5E0", borderRadius:12, padding:"3rem", textAlign:"center", color:"#A8A29E", fontSize:13 }}>
                No requests yet. Providers will appear here after submitting changes.
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {requests.map(req => {
                  const p = req.payload || {};
                  const isPending  = req.status === "pending";
                  const isApproved = req.status === "approved";
                  const statusStyle = {
                    pending:   { bg:"#FAEEDA", color:"#633806", label:"⏳ Pending" },
                    approved:  { bg:"#EAF3DE", color:"#27500A", label:"✓ Approved" },
                    rejected:  { bg:"#FCEBEB", color:"#A32D2D", label:"✕ Rejected" },
                    cancelled: { bg:"#F1EFE8", color:"#78716C", label:"— Cancelled" },
                  }[req.status] || { bg:"#F1EFE8", color:"#78716C", label:req.status };
                  const submitted = new Date(req.submitted_at).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});
                  return (
                    <div key={req.id} style={{ background:"#FFF", border:`0.5px solid ${isPending?"#EF9F27":"#E7E5E0"}`, borderRadius:12, padding:"1.25rem", opacity: isPending ? 1 : 0.8 }}>
                      {/* Header */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                        <div>
                          <div style={{ fontWeight:500, fontSize:14, color:"#1C1917" }}>{req.provider_name || req.user_id}</div>
                          <div style={{ fontSize:11, color:"#A8A29E", marginTop:2 }}>Submitted: {submitted}</div>
                          {req.reviewed_at && <div style={{ fontSize:11, color:"#A8A29E" }}>Reviewed: {new Date(req.reviewed_at).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</div>}
                        </div>
                        <span style={{ fontSize:12, fontWeight:500, padding:"4px 12px", borderRadius:20, background:statusStyle.bg, color:statusStyle.color, whiteSpace:"nowrap" }}>
                          {statusStyle.label}
                        </span>
                      </div>

                      {/* Request payload summary */}
                      <div style={{ background:"#F7F6F2", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:12 }}>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 20px" }}>
                          {[
                            { label:"Tagline",   value: p.tagline || "—" },
                            { label:"City",      value: p.city    || "—" },
                            { label:"WhatsApp",  value: p.whatsapp|| "—" },
                            { label:"Listed",    value: p.is_listed ? "Yes" : "No" },
                            { label:"Diet tags", value: (p.diet_types||[]).join(", ") || "—" },
                            { label:"Areas",     value: (p.areas_served||[]).join(", ") || "—" },
                            { label:"Plans",     value: (p.plans||[]).map(pl=>pl.name).join(", ") || "—" },
                            { label:"Menu photos",value:`${(p.menu_images||[]).length} photo(s)` },
                          ].map(row => (
                            <div key={row.label}>
                              <span style={{ color:"#78716C" }}>{row.label}: </span>
                              <span style={{ color:"#1C1917", fontWeight:500 }}>{row.value}</span>
                            </div>
                          ))}
                        </div>
                        {p.description && (
                          <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid #E8E4DC", color:"#44403C" }}>
                            <span style={{ color:"#78716C" }}>Description: </span>{p.description}
                          </div>
                        )}
                      </div>

                      {/* Rejection note */}
                      {req.status === "rejected" && req.note && (
                        <div style={{ background:"#FCEBEB", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#A32D2D", marginBottom:10 }}>
                          <strong>Rejection reason:</strong> {req.note}
                        </div>
                      )}

                      {/* Action buttons — only for pending */}
                      {isPending && (
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={() => approveRequest(req)}
                            style={{ flex:1, padding:"9px", fontSize:13, fontWeight:500, borderRadius:8, border:"none", background:"#1C1917", color:"#FFF", cursor:"pointer" }}>
                            ✓ Approve — publish changes
                          </button>
                          <button onClick={() => { setRejectTarget(req); setRejectNote(""); }}
                            style={{ padding:"9px 18px", fontSize:13, borderRadius:8, border:"0.5px solid #F09595", background:"#FCEBEB", color:"#A32D2D", cursor:"pointer", fontWeight:500 }}>
                            ✕ Reject
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reject modal */}
            {rejectTarget && (
              <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
                <div className="modal-sm" style={{ background:"#FFF", borderRadius:16, padding:"2rem" }}>
                  <h3 style={{ fontSize:16, fontWeight:600, margin:"0 0 8px" }}>Reject request from {rejectTarget.provider_name}</h3>
                  <p style={{ fontSize:13, color:"#78716C", marginBottom:14 }}>The provider will see your reason so they can correct it.</p>
                  <label style={{ display:"block", fontSize:12, color:"#78716C", marginBottom:5 }}>Reason for rejection</label>
                  <textarea value={rejectNote} onChange={e=>setRejectNote(e.target.value)} rows={3}
                    placeholder="e.g. WhatsApp number is missing, please add it"
                    style={{ width:"100%", padding:"9px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", outline:"none", resize:"vertical", boxSizing:"border-box", marginBottom:14 }}/>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={rejectRequest}
                      style={{ flex:1, padding:"10px", fontSize:13, fontWeight:500, borderRadius:8, border:"none", background:"#A32D2D", color:"#FFF", cursor:"pointer" }}>
                      Reject request
                    </button>
                    <button onClick={()=>setRejectTarget(null)}
                      style={{ padding:"10px 18px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", cursor:"pointer" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── SESSIONS TAB ── */}
        {activeTab === "sessions" && (
          <div>
            <div style={{ display:"flex", gap:8, marginBottom:14, alignItems:"center" }}>
              <input value={sessionSearch} onChange={e => setSessionSearch(e.target.value)}
                placeholder="Search by provider name or ID…"
                style={{ flex:1, padding:"7px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", outline:"none" }} />
              {["All","login","logout"].map(f => (
                <button key={f} onClick={() => setSessionFilter(f)} style={{
                  padding:"7px 14px", fontSize:13, borderRadius:8, cursor:"pointer",
                  border:"0.5px solid #E7E5E0",
                  background: sessionFilter === f ? "#1C1917" : "#FFF",
                  color:      sessionFilter === f ? "#FFF"    : "#78716C",
                  fontWeight: sessionFilter === f ? 500 : 400, textTransform:"capitalize",
                }}>{f}</button>
              ))}
              <button onClick={() => { setLoadingLogs(true); dbFetchSessionLogs(200).then(d => { setSessionLogs(d); setLoadingLogs(false); }); }}
                style={{ padding:"7px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", cursor:"pointer", color:"#44403C" }}>
                ↻ Refresh
              </button>
            </div>

            {loadingLogs ? (
              <div style={{ textAlign:"center", padding:"3rem", color:"#A8A29E", fontSize:13 }}>Loading session logs…</div>
            ) : filteredSessions.length === 0 ? (
              <div style={{ background:"#FFF", borderRadius:12, border:"0.5px solid #E7E5E0", padding:"3rem", textAlign:"center", color:"#A8A29E", fontSize:13 }}>
                No session logs yet. Providers will appear here after they log in.
              </div>
            ) : (
              <div style={{ background:"#FFF", borderRadius:12, border:"0.5px solid #E7E5E0", overflow:"hidden" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ background:"#F7F6F2", borderBottom:"0.5px solid #E7E5E0" }}>
                      {["Event","Provider","Date & Time","Device"].map(h => (
                        <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontWeight:500, fontSize:12, color:"#78716C" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map((log, i) => {
                      const isLogin  = log.event === "login";
                      const evStyle  = isLogin
                        ? { bg:"#EAF3DE", color:"#27500A" }
                        : { bg:"#F1EFE8", color:"#5F5E5A" };
                      const timeStr  = log.time ? new Date(log.time).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";
                      const device   = log.device ? log.device.substring(0, 60) + (log.device.length > 60 ? "…" : "") : "—";
                      return (
                        <tr key={log.id} style={{ borderBottom: i < filteredSessions.length-1 ? "0.5px solid #F3F2EE" : "none" }}>
                          <td style={{ padding:"9px 14px" }}>
                            <span style={{ fontSize:11, fontWeight:500, padding:"3px 10px", borderRadius:20, background:evStyle.bg, color:evStyle.color }}>
                              {isLogin ? "⬆ Login" : "⬇ Logout"}
                            </span>
                          </td>
                          <td style={{ padding:"9px 14px" }}>
                            <div style={{ fontWeight:500, color:"#1C1917" }}>{log.user_name || log.user_id}</div>
                            <div style={{ fontSize:11, color:"#A8A29E" }}>{log.user_id}</div>
                          </td>
                          <td style={{ padding:"9px 14px", color:"#44403C", fontSize:12, whiteSpace:"nowrap" }}>{timeStr}</td>
                          <td style={{ padding:"9px 14px", color:"#78716C", fontSize:11 }}>{device}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── DEBUG TAB ── */}
        {activeTab === "debug" && (
          <div>
            <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:16, flexWrap:"wrap" }}>
              <button onClick={runDebug} disabled={debugging}
                style={{ padding:"9px 20px", fontSize:13, fontWeight:500, borderRadius:8, border:"none",
                  background:debugging?"#78716C":"#1C1917", color:"#FFF", cursor:debugging?"default":"pointer" }}>
                {debugging ? "⏳ Running tests…" : "▶ Run diagnostic"}
              </button>
              {debugLog.length > 0 && (
                <button onClick={()=>setDebugLog([])}
                  style={{ padding:"9px 14px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", cursor:"pointer", color:"#78716C" }}>
                  Clear
                </button>
              )}
              <div style={{ fontSize:12, color:"#78716C" }}>
                Tests Supabase connection, data, and landing page API in one click
              </div>
            </div>

            {debugLog.length === 0 && !debugging && (
              <div style={{ background:"#FFF", border:"0.5px solid #E7E5E0", borderRadius:12, padding:"3rem", textAlign:"center", color:"#A8A29E", fontSize:13 }}>
                Click "Run diagnostic" to check why providers are not showing on the landing page
              </div>
            )}

            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {debugLog.map((log, i) => {
                const S = {
                  pass: { bg:"#EAF3DE", border:"#9FE1CB", icon:"✓", color:"#27500A" },
                  fail: { bg:"#FCEBEB", border:"#F09595", icon:"✕", color:"#A32D2D" },
                  warn: { bg:"#FAEEDA", border:"#EF9F27", icon:"⚠", color:"#633806" },
                  info: { bg:"#E6F1FB", border:"#B5D4F4", icon:"ℹ", color:"#0C447C" },
                }[log.status] || { bg:"#F1EFE8", border:"#E7E5E0", icon:"·", color:"#78716C" };
                return (
                  <div key={i} style={{ background:S.bg, border:`0.5px solid ${S.border}`, borderRadius:10, padding:"12px 16px" }}>
                    <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                      <span style={{ fontSize:14, color:S.color, fontWeight:700, flexShrink:0, marginTop:1 }}>{S.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:S.color, marginBottom:3 }}>{log.title}</div>
                        <div style={{ fontSize:12, color:"#44403C", lineHeight:1.6, marginBottom:log.data?8:0 }}>{log.detail}</div>
                        {log.data && (
                          <div style={{ background:"rgba(255,255,255,0.7)", borderRadius:6, padding:"8px 10px", fontSize:11, fontFamily:"monospace", overflowX:"auto", whiteSpace:"pre-wrap", wordBreak:"break-all", color:"#1C1917", maxHeight:200, overflow:"auto" }}>
                            {JSON.stringify(log.data, null, 2)}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize:10, color:"#A8A29E", flexShrink:0 }}>{log.time}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {debugLog.some(l => l.status==="fail" || l.status==="warn") && (
              <div style={{ marginTop:20, background:"#FFF", borderRadius:12, border:"0.5px solid #E7E5E0", padding:"1.25rem" }}>
                <div style={{ fontSize:13, fontWeight:500, color:"#1C1917", marginBottom:12 }}>How to fix each issue</div>
                {[
                  { title:"If 0 providers listed", fix:"update mealbox_users set is_listed = true where id = 'u1';" },
                  { title:"If columns missing", fix:"Run FIX_COLUMNS_AND_STORAGE.sql in Supabase SQL Editor" },
                  { title:"If REST API → 401", fix:"Open index.html in Notepad and paste the correct anon key into SB_KEY" },
                  { title:"If REST API → 406", fix:"alter table mealbox_users disable row level security;" },
                  { title:"If view missing", fix:"Run MARKETPLACE_SQL.sql in Supabase SQL Editor" },
                ].map(f => (
                  <div key={f.title} style={{ background:"#F7F6F2", borderRadius:8, padding:"10px 14px", marginBottom:8, fontSize:12 }}>
                    <strong style={{ color:"#1C1917" }}>{f.title}:</strong>
                    <code style={{ display:"block", marginTop:5, background:"#1C1917", color:"#7ECDA0", padding:"6px 10px", borderRadius:6, fontSize:12, wordBreak:"break-all" }}>
                      {f.fix}
                    </code>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* ── PROVIDER LISTING TAB ── */}
        {activeTab === "listing" && (() => {
          const DIET_OPTIONS = ["Veg","Non-Veg","Vegan","High Protein","Keto","Diabetic-friendly","Weight Loss","Jain"];
          const MEAL_OPTIONS = ["Breakfast","Lunch","Dinner"];
          const BRAND_COLORS = ["#1A6B3C","#1D9E75","#534AB7","#D85A30","#BA7517","#0C447C","#2D8A52","#A32D2D"];
          const LIST_STEPS   = ["Profile","Plans","Areas","Menu & Logo"];
          const PLACES_KEY   = "AIzaSyAo-h-RBIhZjL1Nf5W23QgCLs1MN6QW-1s";

          // Load Google Maps JS once
          if (!window._gMapsLoaded) {
            window._gMapsLoaded = "loading";
            const sc = document.createElement("script");
            sc.src = "https://maps.googleapis.com/maps/api/js?key=" + PLACES_KEY + "&libraries=places";
            sc.async = true;
            sc.onload = () => { window._gMapsLoaded = "done"; };
            document.head.appendChild(sc);
          }

          // Search predictions via AutocompleteService
          const searchPlaces = async (query) => {
            setPlaceQuery(query);
            setPlaceSelected(false);
            if (!query || query.length < 3) { setPlaceSuggestions([]); return; }
            setPlaceSearching(true);
            const waitForMaps = () => new Promise((res) => {
              const check = () => window._gMapsLoaded === "done" ? res() : setTimeout(check, 300);
              check();
            });
            await waitForMaps();
            const svc = new window.google.maps.places.AutocompleteService();
            svc.getPlacePredictions(
              { input: query, types: ["establishment"], componentRestrictions: { country: "in" } },
              (preds, status) => {
                setPlaceSuggestions(status === "OK" ? preds : []);
                setPlaceSearching(false);
              }
            );
          };

          // Fetch full details on selection
          const selectPlace = (pred) => {
            setPlaceQuery(pred.description);
            setPlaceSuggestions([]);
            setPlaceSearching(true);
            const svc = new window.google.maps.places.PlacesService(document.createElement("div"));
            svc.getDetails({
              placeId: pred.place_id,
              fields: ["name","formatted_address","formatted_phone_number","rating","user_ratings_total","photos","address_components","editorial_summary"],
            }, (place, status) => {
              setPlaceSearching(false);
              if (status !== "OK" || !place) return;
              const comps   = place.address_components || [];
              const cityC   = comps.find(c => c.types.includes("locality") || c.types.includes("administrative_area_level_2"));
              const city    = cityC ? cityC.long_name : "";
              const phone   = (place.formatted_phone_number || "").replace(/[^0-9+]/g, "");
              const desc    = place.editorial_summary?.overview || place.formatted_address || "";
              const photos  = (place.photos || []).slice(0, 3).map(ph => { try { return ph.getUrl({ maxWidth: 800 }); } catch(e) { return ""; } }).filter(Boolean);
              setListForm(f => ({
                ...f,
                name: place.name || f.name,
                city: city || f.city,
                description: desc || f.description,
                whatsapp: phone || f.whatsapp,
                rating: place.rating || f.rating,
                review_count: place.user_ratings_total || f.review_count,
                menu_images: photos.length ? photos : f.menu_images,
              }));
              setPlaceSelected(true);
              setListStep(0);
            });
          };

          const filtered  = listingProviders.filter(p => {
            const q = listingSearch.toLowerCase();
            return !q || p.name?.toLowerCase().includes(q) || p.city?.toLowerCase().includes(q);
          });
          const emptyForm = { name:"",owner:"",city:"",tagline:"",description:"",whatsapp:"",color:"#1A6B3C",logo_url:null,is_listed:true,active:true,diet_types:[],meal_types:[],areas_served:"",plans:[{name:"",desc:"",price:""}],menu_images:[],rating:0,review_count:0 };
          const inp2 = { width:"100%",padding:"9px 12px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",outline:"none",boxSizing:"border-box",fontFamily:"'DM Sans','Segoe UI',sans-serif" };
          const lbl2 = { display:"block",fontSize:12,color:"#78716C",marginBottom:5 };
          const updatePlan  = (i,k,v) => setListForm(f => ({ ...f, plans: f.plans.map((p,idx) => idx===i ? {...p,[k]:v} : p) }));
          const addPlan     = () => setListForm(f => ({ ...f, plans: [...(f.plans||[]), {name:"",desc:"",price:""}] }));
          const removePlan  = (i) => setListForm(f => ({ ...f, plans: f.plans.filter((_,idx) => idx!==i) }));
          const toggleDiet  = (d) => setListForm(f => ({ ...f, diet_types: (f.diet_types||[]).includes(d) ? (f.diet_types||[]).filter(x=>x!==d) : [...(f.diet_types||[]),d] }));
          const toggleMeal  = (m) => setListForm(f => ({ ...f, meal_types: (f.meal_types||[]).includes(m) ? (f.meal_types||[]).filter(x=>x!==m) : [...(f.meal_types||[]),m] }));
          const removeImg   = (url) => setListForm(f => ({ ...f, menu_images: (f.menu_images||[]).filter(u=>u!==url) }));

          return (
            <div>
              {/* Header row */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontSize:14,fontWeight:500,color:"#1C1917"}}>All Marketplace Providers</div>
                  <div style={{fontSize:12,color:"#78716C",marginTop:2}}>Edit profiles, toggle visibility, add businesses without login accounts</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input value={listingSearch} onChange={e=>setListingSearch(e.target.value)} placeholder="Search by name or city…"
                    style={{padding:"7px 12px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",outline:"none",width:190}}/>
                  <button onClick={()=>{setListForm({...emptyForm});setEditListTarget(null);setListStep(0);setShowListForm(true);setListError("");setPlaceQuery("");setPlaceSuggestions([]);setPlaceSelected(false);}}
                    style={{padding:"8px 16px",fontSize:13,fontWeight:500,borderRadius:8,border:"none",background:"#1C1917",color:"#FFF",cursor:"pointer",whiteSpace:"nowrap"}}>
                    + List a Business
                  </button>
                </div>
              </div>

              {/* Table */}
              {listingLoading ? (
                <div style={{textAlign:"center",padding:"3rem",color:"#A8A29E",fontSize:13}}>Loading…</div>
              ) : (
                <div style={{background:"#FFF",borderRadius:12,border:"0.5px solid #E7E5E0",overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:"#F7F6F2",borderBottom:"0.5px solid #E7E5E0"}}>
                        {["Business","City","Diet Types","Listed","Actions"].map(h=>(
                          <th key={h} style={{padding:"10px 14px",textAlign:"left",fontWeight:500,fontSize:12,color:"#78716C",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length===0 && (
                        <tr><td colSpan={5} style={{padding:"3rem",textAlign:"center",color:"#A8A29E"}}>No providers yet</td></tr>
                      )}
                      {filtered.map((p,i) => (
                        <tr key={p.id} style={{borderBottom:i<filtered.length-1?"0.5px solid #F3F2EE":"none"}}>
                          <td style={{padding:"11px 14px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:10}}>
                              {p.logo_url
                                ? <img src={p.logo_url} alt="" style={{width:34,height:34,borderRadius:"50%",objectFit:"cover",flexShrink:0}}/>
                                : <div style={{width:34,height:34,borderRadius:"50%",background:(p.color||"#1A6B3C")+"22",color:p.color||"#1A6B3C",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>
                                    {(p.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                                  </div>
                              }
                              <div>
                                <div style={{fontWeight:500,color:"#1C1917"}}>{p.name}</div>
                                <div style={{fontSize:11,color:"#A8A29E"}}>{p.tagline||""}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{padding:"11px 14px",color:"#78716C"}}>{p.city||"—"}</td>
                          <td style={{padding:"11px 14px"}}>
                            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                              {(p.diet_types||[]).map(d=>(
                                <span key={d} style={{fontSize:10,padding:"2px 7px",borderRadius:20,background:"#EAF3DE",color:"#27500A"}}>{d}</span>
                              ))}
                            </div>
                          </td>
                          <td style={{padding:"11px 14px"}}>
                            <button onClick={()=>toggleListingListed(p)}
                              style={{fontSize:11,fontWeight:500,padding:"3px 12px",borderRadius:20,border:"none",cursor:"pointer",
                                background:p.is_listed?"#EAF3DE":"#F1EFE8",color:p.is_listed?"#27500A":"#78716C"}}>
                              {p.is_listed?"✓ Listed":"Unlisted"}
                            </button>
                          </td>
                          <td style={{padding:"11px 14px"}}>
                            <div style={{display:"flex",gap:6}}>
                              <button onClick={()=>{
                                setListForm({...p,areas_served:(p.areas_served||[]).join(", "),plans:(p.plans||[]).length?p.plans:[{name:"",desc:"",price:""}],diet_types:p.diet_types||[],meal_types:p.meal_types||[],menu_images:p.menu_images||[],logo_url:p.logo_url||null});
                                setEditListTarget(p);setListStep(0);setShowListForm(true);setListError("");setPlaceQuery("");setPlaceSuggestions([]);setPlaceSelected(false);
                              }} style={{fontSize:11,padding:"5px 12px",borderRadius:6,border:"0.5px solid #E7E5E0",background:"#F7F6F2",color:"#44403C",cursor:"pointer"}}>
                                Edit
                              </button>
                              <button onClick={()=>deleteListingProvider(p.id,p.name)}
                                style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:"0.5px solid #F09595",background:"#FCEBEB",color:"#A32D2D",cursor:"pointer"}}>✕</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── 4-Step Edit/Add Modal ── */}
              {showListForm && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:"1rem",fontFamily:"'DM Sans','Segoe UI',sans-serif"}}>
                  <div style={{background:"#FFF",borderRadius:16,width:"100%",maxWidth:640,maxHeight:"92vh",display:"flex",flexDirection:"column"}}>

                    {/* Modal header */}
                    <div style={{padding:"1.25rem 1.5rem",borderBottom:"0.5px solid #E7E5E0",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                      <h3 style={{fontSize:16,fontWeight:600,margin:0}}>{editListTarget?"Edit marketplace profile":"Add new listing"}</h3>
                      <button onClick={()=>{setShowListForm(false);setListForm({});setListStep(0);setPlaceQuery("");setPlaceSuggestions([]);setPlaceSelected(false);}}
                        style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#A8A29E"}}>✕</button>
                    </div>

                    {/* Google Places search — only when adding new */}
                    {!editListTarget && (
                      <div style={{padding:"0.75rem 1.5rem",background:"#F0FAF4",borderBottom:"0.5px solid #C8E6D5",flexShrink:0}}>
                        <div style={{fontSize:11,color:"#27500A",marginBottom:6,fontWeight:600}}>🔍 Search Google to auto-fill details</div>
                        <div style={{position:"relative"}}>
                          <input value={placeQuery} onChange={e=>searchPlaces(e.target.value)}
                            placeholder="Type business name + city, e.g. Spice Kitchen Chennai…"
                            style={{width:"100%",padding:"9px 12px",fontSize:13,borderRadius:8,border:"1.5px solid #1A6B3C",outline:"none",boxSizing:"border-box",fontFamily:"inherit",background:"#FFF"}}/>
                          {placeSearching && <div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"#78716C"}}>Searching…</div>}
                          {placeSuggestions.length > 0 && (
                            <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#FFF",border:"1px solid #E7E5E0",borderRadius:8,boxShadow:"0 4px 20px rgba(0,0,0,0.12)",zIndex:999,maxHeight:200,overflowY:"auto",marginTop:4}}>
                              {placeSuggestions.map((s,i) => (
                                <div key={s.place_id} onClick={()=>selectPlace(s)}
                                  style={{padding:"10px 14px",cursor:"pointer",fontSize:13,borderBottom:i<placeSuggestions.length-1?"0.5px solid #F3F2EE":"none"}}
                                  onMouseEnter={e=>e.currentTarget.style.background="#F7F6F2"}
                                  onMouseLeave={e=>e.currentTarget.style.background="#FFF"}>
                                  <div style={{fontWeight:500,color:"#1C1917"}}>{s.structured_formatting?.main_text||s.description}</div>
                                  <div style={{fontSize:11,color:"#78716C",marginTop:2}}>{s.structured_formatting?.secondary_text||""}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {placeSelected && <div style={{fontSize:11,color:"#27500A",marginTop:5,fontWeight:500}}>✓ Details loaded — review and edit below</div>}
                        {!placeSelected && <div style={{fontSize:10,color:"#78716C",marginTop:4}}>Select from dropdown to auto-fill all fields</div>}
                      </div>
                    )}

                    {/* Step tabs */}
                    <div style={{display:"flex",borderBottom:"0.5px solid #E7E5E0",flexShrink:0}}>
                      {LIST_STEPS.map((s,i)=>(
                        <button key={s} onClick={()=>setListStep(i)}
                          style={{flex:1,padding:"10px 6px",border:"none",borderBottom:`2px solid ${listStep===i?"#1C1917":"transparent"}`,background:"transparent",color:listStep===i?"#1C1917":"#78716C",fontSize:12,fontWeight:listStep===i?500:400,cursor:"pointer"}}>
                          <span style={{fontSize:14}}>{i<listStep?"✓":i+1}</span><br/>{s}
                        </button>
                      ))}
                    </div>

                    {/* Step content */}
                    <div style={{padding:"1.5rem",flex:1,overflowY:"auto"}}>

                      {/* STEP 0: PROFILE */}
                      {listStep===0 && (
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                          <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Business name *</label>
                            <input value={listForm.name||""} onChange={e=>setListForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Spice Kitchen" style={inp2}/>
                          </div>
                          <div><label style={lbl2}>Owner name</label>
                            <input value={listForm.owner||""} onChange={e=>setListForm(f=>({...f,owner:e.target.value}))} placeholder="e.g. Ramesh Kumar" style={inp2}/>
                          </div>
                          <div><label style={lbl2}>City *</label>
                            <input value={listForm.city||""} onChange={e=>setListForm(f=>({...f,city:e.target.value}))} placeholder="e.g. Chennai" style={inp2}/>
                          </div>
                          <div style={{gridColumn:"1/-1"}}><label style={lbl2}>WhatsApp (with country code)</label>
                            <input value={listForm.whatsapp||""} onChange={e=>setListForm(f=>({...f,whatsapp:e.target.value}))} placeholder="919841012345" style={inp2}/>
                          </div>
                          <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Tagline</label>
                            <input value={listForm.tagline||""} onChange={e=>setListForm(f=>({...f,tagline:e.target.value}))} placeholder="Fresh healthy meals delivered daily" style={inp2}/>
                          </div>
                          <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Description</label>
                            <textarea value={listForm.description||""} onChange={e=>setListForm(f=>({...f,description:e.target.value}))} rows={3} placeholder="About this business…" style={{...inp2,resize:"vertical"}}/>
                          </div>
                          <div><label style={lbl2}>Rating (0–5)</label>
                            <input value={listForm.rating||""} onChange={e=>setListForm(f=>({...f,rating:e.target.value}))} placeholder="4.5" style={inp2}/>
                          </div>
                          <div><label style={lbl2}>Review count</label>
                            <input value={listForm.review_count||""} onChange={e=>setListForm(f=>({...f,review_count:e.target.value}))} placeholder="0" style={inp2}/>
                          </div>
                          <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Diet types</label>
                            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                              {DIET_OPTIONS.map(d=>{const on=(listForm.diet_types||[]).includes(d);return(
                                <button key={d} type="button" onClick={()=>toggleDiet(d)}
                                  style={{padding:"4px 12px",fontSize:12,borderRadius:20,border:`1px solid ${on?"#1A6B3C":"#E7E5E0"}`,background:on?"#EAF3DE":"#FFF",color:on?"#27500A":"#78716C",cursor:"pointer"}}>{d}</button>
                              );})}
                            </div>
                          </div>
                          <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Meal types</label>
                            <div style={{display:"flex",gap:6}}>
                              {MEAL_OPTIONS.map(m=>{const on=(listForm.meal_types||[]).includes(m);return(
                                <button key={m} type="button" onClick={()=>toggleMeal(m)}
                                  style={{padding:"4px 14px",fontSize:12,borderRadius:20,border:`1px solid ${on?"#1A6B3C":"#E7E5E0"}`,background:on?"#EAF3DE":"#FFF",color:on?"#27500A":"#78716C",cursor:"pointer"}}>{m}</button>
                              );})}
                            </div>
                          </div>
                          <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Brand color</label>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                              {BRAND_COLORS.map(c=>(
                                <button key={c} type="button" onClick={()=>setListForm(f=>({...f,color:c}))}
                                  style={{width:28,height:28,borderRadius:"50%",background:c,border:`3px solid ${listForm.color===c?"#1C1917":"transparent"}`,cursor:"pointer"}}/>
                              ))}
                            </div>
                          </div>
                          <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",justifyContent:"space-between",background:"#F7F6F2",borderRadius:10,padding:"10px 14px"}}>
                            <div>
                              <div style={{fontSize:13,fontWeight:500,color:"#1C1917"}}>Show on landing page</div>
                              <div style={{fontSize:11,color:"#A8A29E"}}>Toggle to show or hide publicly</div>
                            </div>
                            <button type="button" onClick={()=>setListForm(f=>({...f,is_listed:!f.is_listed}))}
                              style={{padding:"6px 16px",fontSize:13,borderRadius:20,border:"none",cursor:"pointer",fontWeight:500,background:listForm.is_listed?"#EAF3DE":"#FCEBEB",color:listForm.is_listed?"#27500A":"#A32D2D"}}>
                              {listForm.is_listed?"Listed":"Hidden"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* STEP 1: PLANS */}
                      {listStep===1 && (
                        <div>
                          <div style={{fontSize:13,color:"#78716C",marginBottom:14}}>Add up to 4 meal plans with name, description and pricing.</div>
                          {(listForm.plans||[]).map((p,i)=>(
                            <div key={i} style={{background:"#F7F6F2",borderRadius:10,padding:"1rem",marginBottom:12,position:"relative"}}>
                              <div style={{fontSize:12,fontWeight:500,color:"#78716C",marginBottom:10}}>Plan {i+1}</div>
                              {(listForm.plans||[]).length>1 && (
                                <button onClick={()=>removePlan(i)} style={{position:"absolute",top:10,right:10,background:"#FCEBEB",border:"none",borderRadius:6,padding:"3px 8px",fontSize:11,color:"#A32D2D",cursor:"pointer"}}>Remove</button>
                              )}
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                                <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Plan name</label>
                                  <input value={p.name||""} onChange={e=>updatePlan(i,"name",e.target.value)} placeholder="e.g. Weekly Plan" style={inp2}/>
                                </div>
                                <div style={{gridColumn:"1/-1"}}><label style={lbl2}>Description</label>
                                  <input value={p.desc||""} onChange={e=>updatePlan(i,"desc",e.target.value)} placeholder="e.g. 5 days · Lunch + Dinner" style={inp2}/>
                                </div>
                                <div><label style={lbl2}>Price</label>
                                  <input value={p.price||""} onChange={e=>updatePlan(i,"price",e.target.value)} placeholder="e.g. ₹1,400" style={inp2}/>
                                </div>
                              </div>
                            </div>
                          ))}
                          {(listForm.plans||[]).length<4 && (
                            <button onClick={addPlan} style={{padding:"8px",fontSize:13,borderRadius:8,border:"0.5px dashed #E7E5E0",background:"transparent",color:"#78716C",cursor:"pointer",width:"100%"}}>
                              + Add plan ({(listForm.plans||[]).length}/4)
                            </button>
                          )}
                        </div>
                      )}

                      {/* STEP 2: AREAS */}
                      {listStep===2 && (
                        <div>
                          <div style={{fontSize:13,color:"#78716C",marginBottom:14}}>Enter delivery areas separated by commas.</div>
                          <label style={lbl2}>Areas served (comma separated)</label>
                          <textarea value={typeof listForm.areas_served==="string"?listForm.areas_served:(listForm.areas_served||[]).join(", ")}
                            onChange={e=>setListForm(f=>({...f,areas_served:e.target.value}))}
                            rows={3} placeholder="Anna Nagar, T Nagar, Velachery, Adyar" style={{...inp2,resize:"vertical"}}/>
                          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10}}>
                            {(typeof listForm.areas_served==="string"?listForm.areas_served.split(",").map(s=>s.trim()).filter(Boolean):(listForm.areas_served||[])).map(a=>(
                              <span key={a} style={{fontSize:12,padding:"3px 10px",borderRadius:20,background:"#EAF3DE",color:"#27500A"}}>📍 {a}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* STEP 3: MENU & LOGO */}
                      {listStep===3 && (
                        <div>
                          {/* Logo */}
                          <div style={{marginBottom:24}}>
                            <label style={lbl2}>Business Logo <span style={{color:"#A8A29E",fontSize:11}}>(shown instead of initials on landing page)</span></label>
                            <div style={{display:"flex",alignItems:"center",gap:14}}>
                              {listForm.logo_url
                                ? <img src={listForm.logo_url} alt="Logo" style={{width:64,height:64,borderRadius:"50%",objectFit:"cover",border:"2px solid #E7E5E0"}}/>
                                : <div style={{width:64,height:64,borderRadius:"50%",background:(listForm.color||"#1A6B3C")+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:listForm.color||"#1A6B3C",border:"2px dashed #E7E5E0"}}>
                                    {(listForm.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                                  </div>
                              }
                              <div>
                                <label style={{display:"inline-block",padding:"7px 16px",fontSize:12,fontWeight:500,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#F7F6F2",color:"#44403C",cursor:"pointer"}}>
                                  {logoUploading?"Uploading…":"Upload logo"}
                                  <input type="file" accept="image/*" onChange={uploadListingLogo} style={{display:"none"}} disabled={logoUploading}/>
                                </label>
                                {listForm.logo_url && (
                                  <button onClick={()=>setListForm(f=>({...f,logo_url:null}))}
                                    style={{marginLeft:8,fontSize:12,padding:"7px 12px",borderRadius:8,border:"0.5px solid #F09595",background:"#FCEBEB",color:"#A32D2D",cursor:"pointer"}}>Remove</button>
                                )}
                                <div style={{fontSize:11,color:"#A8A29E",marginTop:5}}>JPG/PNG · max 3MB · Square recommended</div>
                              </div>
                            </div>
                          </div>
                          {/* Menu photos */}
                          <label style={lbl2}>Menu Photos <span style={{color:"#A8A29E",fontSize:11}}>(up to 4)</span></label>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:8}}>
                            {(listForm.menu_images||[]).map((url,i)=>(
                              <div key={i} style={{position:"relative",borderRadius:10,overflow:"hidden",aspectRatio:"4/3",background:"#F7F6F2"}}>
                                <img src={url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                                <button onClick={()=>removeImg(url)} style={{position:"absolute",top:6,right:6,background:"rgba(0,0,0,0.55)",border:"none",borderRadius:"50%",width:26,height:26,color:"#FFF",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                              </div>
                            ))}
                            {(listForm.menu_images||[]).length<4 && (
                              <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,borderRadius:10,border:"1.5px dashed #E7E5E0",aspectRatio:"4/3",cursor:"pointer",background:"#F7F6F2",color:"#78716C",fontSize:12}}>
                                {listUploading?"Uploading…":<><span style={{fontSize:22}}>📷</span><span>Upload photo</span></>}
                                <input type="file" accept="image/*" onChange={uploadListingMenuImg} style={{display:"none"}} disabled={listUploading}/>
                              </label>
                            )}
                          </div>
                          <div style={{fontSize:11,color:"#A8A29E"}}>{(listForm.menu_images||[]).length}/4 photos</div>
                        </div>
                      )}
                    </div>

                    {/* Modal footer */}
                    {listError && <div style={{margin:"0 1.5rem",padding:"8px 12px",background:"#FCEBEB",border:"0.5px solid #F09595",borderRadius:8,fontSize:12,color:"#A32D2D"}}>{listError}</div>}
                    <div style={{padding:"1rem 1.5rem",borderTop:"0.5px solid #E7E5E0",display:"flex",gap:8,justifyContent:"space-between",flexShrink:0}}>
                      <button onClick={()=>setListStep(s=>Math.max(0,s-1))} disabled={listStep===0}
                        style={{padding:"9px 20px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",color:"#44403C",cursor:listStep===0?"default":"pointer",opacity:listStep===0?0.4:1}}>
                        ← Back
                      </button>
                      <div style={{display:"flex",gap:8}}>
                        {listStep < 3 ? (
                          <button onClick={()=>setListStep(s=>s+1)}
                            style={{padding:"9px 24px",fontSize:13,fontWeight:500,borderRadius:8,border:"none",background:"#1C1917",color:"#FFF",cursor:"pointer"}}>
                            Next →
                          </button>
                        ) : (
                          <button onClick={saveListingProvider} disabled={listSaving}
                            style={{padding:"9px 24px",fontSize:13,fontWeight:500,borderRadius:8,border:"none",background:listSaving?"#78716C":"#1D9E75",color:"#FFF",cursor:listSaving?"default":"pointer"}}>
                            {listSaving?"Saving…":editListTarget?"Save changes":"Add to marketplace"}
                          </button>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── FORM SUBMISSIONS TAB ── */}
        {activeTab === "enquiries" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{fontSize:14,fontWeight:500,color:"#1C1917"}}>Business Enquiry Submissions</div>
                <div style={{fontSize:12,color:"#78716C",marginTop:2}}>Leads from the "List your business" form on the landing page</div>
              </div>
              <button onClick={()=>{setEnquiryLoading(true);dbFetchBusinessForms().then(d=>{setEnquiries(d);setEnquiryLoading(false);});}}
                style={{padding:"7px 12px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",cursor:"pointer",color:"#44403C"}}>
                ↻ Refresh
              </button>
            </div>
            {enquiryLoading ? (
              <div style={{textAlign:"center",padding:"3rem",color:"#A8A29E",fontSize:13}}>Loading…</div>
            ) : enquiries.length===0 ? (
              <div style={{background:"#FFF",border:"0.5px solid #E7E5E0",borderRadius:12,padding:"3rem",textAlign:"center",color:"#A8A29E",fontSize:13}}>
                No enquiries yet. They will appear here when businesses submit the "List your business" form.
              </div>
            ) : (
              <div style={{background:"#FFF",borderRadius:12,border:"0.5px solid #E7E5E0",overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{background:"#F7F6F2",borderBottom:"0.5px solid #E7E5E0"}}>
                      {["Business Name","WhatsApp","Phone","City","Submitted","Status","Action"].map(h=>(
                        <th key={h} style={{padding:"10px 14px",textAlign:"left",fontWeight:500,fontSize:12,color:"#78716C",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((e,i)=>{
                      const ST = {
                        new:       {bg:"#E6F1FB",color:"#0C447C",label:"New"},
                        contacted: {bg:"#FAEEDA",color:"#633806",label:"Contacted"},
                        done:      {bg:"#EAF3DE",color:"#27500A",label:"Done"},
                      }[e.status]||{bg:"#F1EFE8",color:"#78716C",label:e.status};
                      const dt = e.submitted_at ? new Date(e.submitted_at).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—";
                      const wa = e.whatsapp?.replace(/\D/g,"");
                      return (
                        <tr key={e.id} style={{borderBottom:i<enquiries.length-1?"0.5px solid #F3F2EE":"none"}}>
                          <td style={{padding:"11px 14px",fontWeight:500,color:"#1C1917"}}>{e.business_name||"—"}</td>
                          <td style={{padding:"11px 14px",fontSize:12}}>
                            {wa ? <a href={`https://wa.me/${wa.startsWith("91")?wa:"91"+wa}`} target="_blank" rel="noopener noreferrer"
                              style={{color:"#25D366",fontWeight:500,textDecoration:"none"}}>
                              💬 {e.whatsapp}
                            </a> : "—"}
                          </td>
                          <td style={{padding:"11px 14px",color:"#78716C",fontSize:12}}>{e.phone||"—"}</td>
                          <td style={{padding:"11px 14px",color:"#78716C"}}>{e.city||"—"}</td>
                          <td style={{padding:"11px 14px",color:"#78716C",fontSize:11,whiteSpace:"nowrap"}}>{dt}</td>
                          <td style={{padding:"11px 14px"}}>
                            <span style={{fontSize:11,fontWeight:500,padding:"3px 10px",borderRadius:20,background:ST.bg,color:ST.color}}>{ST.label}</span>
                          </td>
                          <td style={{padding:"11px 14px"}}>
                            <select value={e.status} onChange={async(ev)=>{
                              await dbUpdateEnquiryStatus(e.id, ev.target.value);
                              setEnquiries(prev=>prev.map(x=>x.id===e.id?{...x,status:ev.target.value}:x));
                            }} style={{padding:"4px 8px",fontSize:12,borderRadius:6,border:"0.5px solid #E7E5E0",background:"#FFF",cursor:"pointer"}}>
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="done">Done</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      {/* ── WHATSAPP LEADS TAB ── */}
        {activeTab === "waleads" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{fontSize:14,fontWeight:500,color:"#1C1917"}}>WhatsApp Leads</div>
                <div style={{fontSize:12,color:"#78716C",marginTop:2}}>Customers who clicked "Chat on WhatsApp" on provider pages</div>
              </div>
              <button
                onClick={()=>{ setWaLeadsLoading(true); dbFetchWaLeads().then(d=>{setWaLeads(d);setWaLeadsLoading(false);}); }}
                style={{padding:"7px 12px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",cursor:"pointer",color:"#44403C"}}>
                ↻ Refresh
              </button>
            </div>

            {waLeadsLoading ? (
              <div style={{textAlign:"center",padding:"3rem",color:"#A8A29E",fontSize:13}}>Loading…</div>
            ) : waLeads.length === 0 ? (
              <div style={{background:"#FFF",border:"0.5px solid #E7E5E0",borderRadius:12,padding:"3rem",textAlign:"center",color:"#A8A29E",fontSize:13}}>
                No WhatsApp leads yet. They will appear here when customers click the WhatsApp button on provider pages.
              </div>
            ) : (
              <div style={{background:"#FFF",borderRadius:12,border:"0.5px solid #E7E5E0",overflowX:"auto"}}>
                {/* Summary bar */}
                <div style={{display:"flex",gap:24,padding:"14px 18px",borderBottom:"0.5px solid #E7E5E0",background:"#F7F6F2",borderRadius:"12px 12px 0 0"}}>
                  <div style={{fontSize:12,color:"#78716C"}}>
                    Total leads: <strong style={{color:"#1C1917"}}>{waLeads.length}</strong>
                  </div>
                  <div style={{fontSize:12,color:"#78716C"}}>
                    Unique providers contacted: <strong style={{color:"#1C1917"}}>
                      {new Set(waLeads.map(w=>w.provider_name).filter(Boolean)).size}
                    </strong>
                  </div>
                  <div style={{fontSize:12,color:"#78716C"}}>
                    Today: <strong style={{color:"#1C1917"}}>
                      {waLeads.filter(w=>{
                        const d = new Date(w.clicked_at);
                        const t = new Date();
                        return d.getDate()===t.getDate() && d.getMonth()===t.getMonth() && d.getFullYear()===t.getFullYear();
                      }).length}
                    </strong>
                  </div>
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{background:"#F7F6F2",borderBottom:"0.5px solid #E7E5E0"}}>
                      {["Date & Time","Customer Name","Mobile","Customer City","Provider Contacted","Provider City"].map(h=>(
                        <th key={h} style={{padding:"10px 14px",textAlign:"left",fontWeight:500,fontSize:12,color:"#78716C",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {waLeads.map((w,i)=>{
                      const dt = w.clicked_at
                        ? new Date(w.clicked_at).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})
                        : "—";
                      const isToday = w.clicked_at && (()=>{
                        const d = new Date(w.clicked_at), t = new Date();
                        return d.getDate()===t.getDate() && d.getMonth()===t.getMonth() && d.getFullYear()===t.getFullYear();
                      })();
                      return (
                        <tr key={w.id} style={{borderBottom:i<waLeads.length-1?"0.5px solid #F3F2EE":"none",background:isToday?"#FAFDF7":"#FFF"}}>
                          <td style={{padding:"11px 14px",fontSize:11,color:"#78716C",whiteSpace:"nowrap"}}>
                            {isToday && <span style={{fontSize:10,fontWeight:700,background:"#EAF3DE",color:"#27500A",padding:"1px 6px",borderRadius:10,marginRight:6}}>Today</span>}
                            {dt}
                          </td>
                          <td style={{padding:"11px 14px",fontWeight:500,color:"#1C1917"}}>{w.user_name||"—"}</td>
                          <td style={{padding:"11px 14px"}}>
                            {w.mobile
                              ? <a href={`https://wa.me/91${w.mobile.replace(/\D/g,"").slice(-10)}`} target="_blank" rel="noopener noreferrer"
                                  style={{color:"#25D366",fontWeight:500,textDecoration:"none"}}>
                                  💬 {w.mobile}
                                </a>
                              : "—"}
                          </td>
                          <td style={{padding:"11px 14px",color:"#78716C"}}>{w.user_city||"—"}</td>
                          <td style={{padding:"11px 14px",color:"#1C1917",fontWeight:500}}>
                            <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
                              <span style={{width:8,height:8,borderRadius:"50%",background:"#25D366",display:"inline-block",flexShrink:0}}></span>
                              {w.provider_name||"—"}
                            </span>
                          </td>
                          <td style={{padding:"11px 14px",color:"#78716C"}}>{w.provider_city||"—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      {/* ── LEAD DASHBOARD TAB ── */}
        {activeTab === "leaddash" && (
          <LeadDashboard
            leads={waLeads}
            loading={waLeadsLoading}
            onRefresh={() => { setWaLeadsLoading(true); dbFetchWaLeads().then(d=>{setWaLeads(d);setWaLeadsLoading(false);}); }}
            onUpdateStatus={dbUpdateWaLeadStatus}
          />
        )}

      {/* ── Change Password Modal ── */}
      {showChangePw && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
          <div className="modal-sm" style={{ background:"#FFF", borderRadius:16, padding:"2rem" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem" }}>
              <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>Change admin password</h3>
              <button onClick={() => setShowChangePw(false)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#A8A29E" }}>✕</button>
            </div>
            {[
              { label:"Current password", key:"current" },
              { label:"New password",     key:"next"    },
              { label:"Confirm new",      key:"confirm" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:"#78716C", marginBottom:5 }}>{f.label}</label>
                <input type="password" value={pwForm[f.key]}
                  onChange={e => { setPwForm(p => ({ ...p, [f.key]: e.target.value })); setPwError(""); }}
                  style={{ width:"100%", padding:"8px 10px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", outline:"none", boxSizing:"border-box" }} />
              </div>
            ))}
            {pwError && <div style={{ background:"#FCEBEB", border:"0.5px solid #F09595", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#A32D2D", marginBottom:12 }}>{pwError}</div>}
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={changePassword} style={{ flex:1, padding:"9px", fontSize:13, fontWeight:500, borderRadius:8, border:"none", background:"#1C1917", color:"#FFF", cursor:"pointer" }}>
                Update password
              </button>
              <button onClick={() => setShowChangePw(false)} style={{ padding:"9px 18px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* User form modal */}
      {showForm && editTarget && (
        <UserFormModal
          user={editTarget}
          isEdit={!!providerUsers.find(u => u.id === editTarget.id)}
          onSave={saveUser}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          existingLogins={users}
        />
      )}

      </div>
    </div>
  );
}
